import * as React from 'react';
import {Button, Icon, InputNumber, Modal, Table, Tooltip} from 'antd';
import {ColumnProps, CompareFn, SortOrder} from 'antd/lib/table';

import ButtonBar from '../common/ButtonBar';
import locales from '../locales';
import {numberGroupFormatter, renderFixedPriceColumn} from '../utils/number-utils';
import {InventoryStock, InventoryStockItem, Item, MaintenanceHistoryItemEntry} from '../api/dtos/dtos';
import InventoryStockSelector from './InventoryStockSelector';
import {simpleColumn} from "../utils/table-utils";
import ButtonGroup from "antd/lib/button/button-group";
import {showDeleteConfirm} from '../utils/dialogs';
import {Link} from "react-router-dom";
import * as paths from "../paths";
import MaintenanceEntryGroupModal from "./MaintenanceEntryGroupModal";

export interface MaintenanceEntryEditorProps {
  disabled?: boolean;
  renterCost: boolean;
  item?: Item;
  entries: MaintenanceHistoryItemEntry[];
  onEntryDelete: (entry: MaintenanceHistoryItemEntry) => void;
  onEntryUpdate: (entry: MaintenanceHistoryItemEntry) => void;
  onEntriesToAdd: (entries: MaintenanceHistoryItemEntry[]) => void;
}

interface State {
  inventoryStockSelectorVisible: boolean;
  costEditVisible: boolean;
  discountEditVisible: boolean;
  discountPercent?: number;
  editingEntry?: MaintenanceHistoryItemEntry;
  costInfoEntry?: MaintenanceHistoryItemEntry;
}

interface InfiniteStockSelection {
  stock: InventoryStock;
  count: number;
}

export default class MaintenanceEntryEditor extends React.Component<MaintenanceEntryEditorProps, State> {
  public state: State = {
    inventoryStockSelectorVisible: false,
    costEditVisible: false,
    discountEditVisible: false,
  };

  public render(): React.ReactNode {
    const {costInfoEntry} = this.state;
    const entries = this.props.entries.sort(abcSorter)

    const columns: Array<ColumnProps<MaintenanceHistoryItemEntry>> = [
      simpleColumn(locales.item_number, this.renderInventoryName, 150),
      simpleColumn(locales.appellation, entry => entry.name || '-'),
    ];

    if (this.props.renterCost) {
      columns.push(simpleColumn(locales.initial_cost, this.renderInitialInventoryCost))
    }

    columns.push(...[
      simpleColumn(locales.amount, this.renderAmount, 130),
      simpleColumn(locales.unit_price, this.renderUnitPrize),
    ])

    if (this.props.renterCost) {
      columns.push(
        simpleColumn(locales.discount, this.renderDiscount, 130)
      );
    }

    columns.push(...[
      simpleColumn(locales.total, this.renderTotal, 160),
      {
        title: '',
        key: 'actions',
        width: 48,
        render: this.renderActionColumn
      },
    ]);

    return (
      <>
        <ButtonBar style={{marginBottom: 8}}>
          <Button size="small" type="primary" icon="plus" onClick={this.onAddItemButtonClick}
                  disabled={this.props.disabled}>
            {locales.add}
          </Button>
        </ButtonBar>

        <Modal
          title={locales.inventory_stock_items}
          visible={this.state.inventoryStockSelectorVisible}
          footer={null}
          onCancel={this.onInventoryStockSelectorCancel}
          width="auto"
          style={{margin: '0 24px'}}
          destroyOnClose={true}
        >
          <InventoryStockSelector
            onSelect={this.onInventoryStockSelected}
          />
        </Modal>

        <Modal
          title={locales.unit_price}
          visible={this.state.costEditVisible}
          onCancel={this.onCostEditCancel}
          cancelText={locales.cancel}
          okText={locales.save}
          onOk={this.onCostEditConfirm}
          destroyOnClose={true}
        >
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8}}>
            <InputNumber
              style={{width: '50%'}}
              min={0}
              precision={0}
              value={this.state.editingEntry?.cost}
              placeholder={locales.unit_price}
              formatter={numberGroupFormatter}
              onChange={this.onCostDraftChange}
            />
            <span style={{whiteSpace: 'nowrap'}}>{locales.app_currency}</span>
          </div>
        </Modal>

        {costInfoEntry &&
            <MaintenanceEntryGroupModal
                costInfoEntry={costInfoEntry}
                onEntryCostInfoCancel={this.onEntryCostInfoCancel}
            />
        }

        <Modal
          title={locales.discount}
          visible={this.state.discountEditVisible}
          onCancel={this.onDiscountEditCancel}
          cancelText={locales.cancel}
          okText={locales.save}
          onOk={this.onDiscountEditConfirm}
          destroyOnClose={true}
        >
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8}}>
            <InputNumber
              style={{width: '50%'}}
              min={0}
              max={100}
              precision={0}
              value={this.state.editingEntry?.discount}
              placeholder={locales.discount}
              onChange={this.onDiscountDraftChange}
            />
            <span style={{whiteSpace: 'nowrap'}}>%</span>
          </div>
        </Modal>

        <Table
          style={{marginBottom: 16}}
          dataSource={entries}
          columns={columns}
          rowKey={this.getRowKey}
          bordered={true}
          size="small"
          pagination={false}
          scroll={{x: 1024}}
        />
      </>
    );
  }

  private getRowKey = (_: MaintenanceHistoryItemEntry, index: number): string => {
    return index.toString();
  };

  private renderInventoryName = (entry: MaintenanceHistoryItemEntry): React.ReactNode => {
    if (entry.inventoryStockId) {
      return <Link to={{
        pathname: paths.viewInventoryStockPath(entry.inventoryStockId),
        state: {
          stateFilter: {
            targetType: 'maintenance',
            item: this.props.item,
          },
        },
      }}>
        {entry.inventoryNumber}
      </Link>;
    }
    return (entry.inventoryNumber?.length ?? 0) > 0 ? entry.inventoryNumber : '-';

  }

  private renderUnitPrize = (entry: MaintenanceHistoryItemEntry): React.ReactNode => {
    const {renterCost} = this.props;
    const costWasEdited = renterCost && entry.originalCost.toFixed() !== entry.cost.toFixed();
    const hasDifferentPriceGroups = (entry.groupedItems?.length || 0) > 1;
    return (
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8}}>
        <div style={{display: 'flex', flexDirection: 'column'}}>
          {costWasEdited && (
            <span style={{textDecoration: 'line-through', fontSize: '0.8em', color: '#999'}}>
              {renderFixedPriceColumn(entry.originalCost)}
            </span>
          )}
          <span>{renderFixedPriceColumn(entry.cost)}</span>
        </div>
        {renterCost && (
          <ButtonGroup>
            {hasDifferentPriceGroups && (
              <Tooltip title={locales.maintenance_history_entry_group_info}>
                <Button style={{padding: 0}} onClick={this.onEntryCostInfoOpen(entry)}>
                  <Icon type="warning" style={{color: 'red', width: 30}}/>
                </Button>
              </Tooltip>
            )}
            <Button
              icon="edit"
              title={locales.edit}
              disabled={this.props.disabled}
              onClick={this.onCostEditOpen(entry)}
            />
          </ButtonGroup>
        )}
      </div>
    );
  }

  private renderInitialInventoryCost = (entry: MaintenanceHistoryItemEntry): React.ReactNode => {
    const hasDifferentPriceGroups = (entry.groupedItems?.length || 0) > 1;
    const initialInventoryCost = entry.groupedItems?.reduce(
      (max, {initialCost}) => Math.max(max, initialCost),
      entry.groupedItems[0].initialCost
    );

    const value = initialInventoryCost !== undefined ? renderFixedPriceColumn(initialInventoryCost) : '-';
    return (
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8}}>
        <span>{value}</span>
        {hasDifferentPriceGroups && (
          <Tooltip title={locales.maintenance_history_entry_group_info}>
            <Button style={{padding: 0}} onClick={this.onEntryCostInfoOpen(entry)}>
              <Icon type="warning" style={{color: 'red', width: 30}}/>
            </Button>
          </Tooltip>
        )}
      </div>
    );
  }

  private renderAmount = (entry: MaintenanceHistoryItemEntry): React.ReactNode => {
    return `${numberGroupFormatter(entry.amount)} ${locales.pcs}`;
  }

  private renderDiscount = (entry: MaintenanceHistoryItemEntry): React.ReactNode => {
    return (
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8}}>
        {entry.discount ? `${entry.discount} %` : '-'}

        <ButtonGroup>
          <Button
            icon="edit"
            title={locales.edit}
            disabled={this.props.disabled}
            onClick={this.onDiscountEditOpen(entry)}
          />
        </ButtonGroup>
      </div>
    );
  }

  private renderTotal = (entry: MaintenanceHistoryItemEntry): React.ReactNode => {
    const originalTotal = entry.originalCost * entry.amount;
    const currentTotal = entry.cost * entry.amount * (1 - (entry.discount || 0) / 100);
    const costWasEdited = this.props.renterCost && (entry.cost !== entry.originalCost || entry.discount !== 0);

    return (
      <div style={{display: 'flex', flexDirection: 'column'}}>
        {costWasEdited && (
          <span style={{textDecoration: 'line-through', fontSize: '0.8em', color: '#999'}}>
            {renderFixedPriceColumn(originalTotal)}
          </span>
        )}
        <span>{renderFixedPriceColumn(currentTotal)}</span>
      </div>
    );
  }

  private renderActionColumn = (entry: MaintenanceHistoryItemEntry): React.ReactNode => {
    return (
      <div className="action-column" style={{textAlign: 'right'}}>
        <ButtonGroup>
          <Button
            icon="delete"
            title={locales.delete}
            disabled={this.props.disabled}
            onClick={this.onDeleteGroupedEntryButtonClick(entry)}
          />
        </ButtonGroup>
      </div>
    );
  };


  private onAddItemButtonClick = () => {
    if (this.props.disabled) {
      return;
    }
    this.setState({inventoryStockSelectorVisible: true});
  };

  private onInventoryStockSelectorCancel = () => {
    this.setState({inventoryStockSelectorVisible: false});
  };

  private onInventoryStockSelected = (stockItems: InventoryStockItem[], infiniteSelections: InfiniteStockSelection[]) => {
    const newEntries = stockItems.map(this.toMaintenanceHistoryEntry);
    const newInfiniteEntries: MaintenanceHistoryItemEntry[] = [];
    infiniteSelections.forEach(selection => {
      newInfiniteEntries.push({
          infiniteInventoryStockId: selection.stock.id,
          inventoryNumber: selection.stock.inventoryNumber,
          name: selection.stock.name,
          amount: selection.count,
          cost: selection.stock.infiniteStockCost! * (1 + selection.stock.infiniteStockProfitMargin! / 100),
          originalCost: selection.stock.infiniteStockCost!,
        }
      );
    });

    this.setState({
        inventoryStockSelectorVisible: false,
      }, () => this.props.onEntriesToAdd([...newEntries, ...newInfiniteEntries])
    );
  };

  private toMaintenanceHistoryEntry = (stockItem: InventoryStockItem): MaintenanceHistoryItemEntry => ({
    inventoryStockItem: stockItem,
    inventoryNumber: stockItem.inventoryStock?.inventoryNumber || '',
    name: stockItem.inventoryStock?.name || '',
    amount: 1,
    cost: this.getInventoryStockEntryCost(stockItem),
    originalCost: this.getInventoryStockEntryCost(stockItem),
  });

  private getInventoryStockEntryCost = (stockItem: InventoryStockItem): number => {
    if (!this.props.renterCost) {
      return stockItem.hufCost;
    }
    return stockItem.hufCost * (1 + stockItem.profitMargin / 100);
  }

  private onCostEditOpen = (entry: MaintenanceHistoryItemEntry) => () => {
    if (this.props.disabled || !this.props.renterCost) {
      return;
    }
    this.setState({
      costEditVisible: true,
      editingEntry: entry,
    });
  };

  private onEntryCostInfoOpen = (entry: MaintenanceHistoryItemEntry) => () => {
    this.setState({
      costInfoEntry: entry,
    });
  };

  private onEntryCostInfoCancel = () => {
    this.setState({
      costInfoEntry: undefined,
    });
  };

  private onCostEditCancel = () => {
    this.setState({
      costEditVisible: false,
      editingEntry: undefined,
    });
  };

  private onCostDraftChange = (value: number | undefined) => {
    this.setState((prevState) => {
      if (!prevState.editingEntry) {
        return null;
      }
      return {
        editingEntry: {
          ...prevState.editingEntry,
          cost: value ?? prevState.editingEntry.cost,
        },
      };
    });
  };

  private onCostEditConfirm = () => {
    const {editingEntry} = this.state;
    if (!editingEntry) {
      return;
    }

    this.setState({editingEntry: undefined, costEditVisible: false}, () => this.props.onEntryUpdate(editingEntry));
  };

  private onDiscountEditOpen = (entry: MaintenanceHistoryItemEntry) => () => {
    if (this.props.disabled) {
      return;
    }
    this.setState({
      discountEditVisible: true,
      editingEntry: entry,
    });
  };

  private onDiscountEditCancel = () => {
    this.setState({
      discountEditVisible: false,
      editingEntry: undefined,
    });
  };

  private onDiscountEditConfirm = () => {
    const {editingEntry} = this.state;
    if (!editingEntry) {
      return;
    }

    this.setState({editingEntry: undefined, discountEditVisible: false}, () => this.props.onEntryUpdate(editingEntry));
  };

  private onDiscountDraftChange = (value: number | undefined) => {
    this.setState((prevState) => {
      if (!prevState.editingEntry) {
        return null;
      }
      return {
        editingEntry: {
          ...prevState.editingEntry,
          discount: value ?? prevState.editingEntry.discount,
        },
      };
    });
  };

  private onDeleteGroupedEntryButtonClick = (entry: MaintenanceHistoryItemEntry) => () => {
    if (this.props.disabled) {
      return;
    }
    showDeleteConfirm(() => this.deleteGroupedEntry(entry));
  };

  private deleteGroupedEntry = (entry: MaintenanceHistoryItemEntry) => {
    this.props.onEntryDelete(entry);
  };
}

const abcSorter: CompareFn<MaintenanceHistoryItemEntry> = (a: MaintenanceHistoryItemEntry, b: MaintenanceHistoryItemEntry, _?: SortOrder) => {
  const aNumber = a.inventoryNumber?.trim();
  const bNumber = b.inventoryNumber?.trim();

  if (!aNumber && !bNumber) {
    return 0;
  }
  if (!aNumber) {
    return 1;
  }
  if (!bNumber) {
    return -1;
  }

  if (aNumber < bNumber) {
    return -1;
  }
  return 1;
};



// WEBPACK FOOTER //
// ./src/maintenance/MaintenanceEntryEditor.tsx